import {NodeType} from "crqlar-react-common/dist/models/automation/automation";

export interface NodeOutput {
    name: string;
    /** Optional presentation metadata; references still use the unchanged output name. */
    label?: string;
    valueKind?: 'language' | 'country' | 'nationality';
    type: 'string' | 'number' | 'boolean' | 'object' | 'date' | 'array';
    description?: string;
    required?: boolean;
    /** A closed value set; If offers it as a select for equality rules. Values are what the engine emits, labels are translation keys. */
    options?: Array<{value: string; label: string}>;
}

export interface NodeOutputDefinition {
    nodeType: NodeType;
    outputs: NodeOutput[];
}

const reservationOutputs: NodeOutput[] = [
    {name: 'dashboardUrl', type: 'string', description: 'Link to the organization dashboard', required: false},
    {name: 'id', type: 'string', description: 'Reservation ID', required: true},
    {name: 'code', type: 'string', description: 'Reservation code/number', required: false},
    {name: 'sectorId', type: 'string', description: 'Sector ID', required: false},
    {name: 'serviceId', type: 'string', description: 'Service ID', required: false},
    {
        name: 'status',
        type: 'string',
        description: 'Reservation status (PENDING, CONFIRMED, etc.)',
        required: true
    },
    {name: 'reservationGroupId', type: 'string', description: 'Reservation group ID', required: false},

    {name: 'primaryProfileId', type: 'string', description: 'Primary profile ID', required: false},
    {name: 'profileIds', type: 'array', description: 'Array of all profile IDs', required: false},

    {name: 'resourceIds', type: 'array', description: 'Array of resource IDs', required: false},

    {name: 'expectedStartDate', type: 'date', description: 'Expected start date/time', required: false},
    {name: 'expectedStartDay', type: 'string', description: 'Expected start day (YYYY-MM-DD)', required: false},
    {name: 'expectedStartTime', type: 'string', description: 'Expected start time (HH:mm)', required: false},
    {name: 'expectedEndDate', type: 'date', description: 'Expected end date/time', required: false},
    {name: 'expectedEndDay', type: 'string', description: 'Expected end day (YYYY-MM-DD)', required: false},
    {name: 'expectedEndTime', type: 'string', description: 'Expected end time (HH:mm)', required: false},
    {name: 'actualStartDate', type: 'date', description: 'Actual start date/time', required: false},
    {name: 'actualStartDay', type: 'string', description: 'Actual start day (YYYY-MM-DD)', required: false},
    {name: 'actualStartTime', type: 'string', description: 'Actual start time (HH:mm)', required: false},
    {name: 'actualEndDate', type: 'date', description: 'Actual end date/time', required: false},
    {name: 'actualEndDay', type: 'string', description: 'Actual end day (YYYY-MM-DD)', required: false},
    {name: 'actualEndTime', type: 'string', description: 'Actual end time (HH:mm)', required: false},

    {name: 'cancelledDate', type: 'date', description: 'Cancellation date', required: false},
    {name: 'cancellationReason', type: 'string', description: 'Cancellation reason', required: false},
    {name: 'deadlineDate', type: 'date', description: 'Deadline date', required: false},
    {name: 'deadlineDay', type: 'string', description: 'Deadline day (YYYY-MM-DD)', required: false},

    {name: 'additionalInfo.marketCode', type: 'string', description: 'Market code', required: false},
    {
        name: 'additionalInfo.sourceOfBusiness',
        type: 'string',
        description: 'Source of business',
        required: false
    },
    {
        name: 'additionalInfo.communicationChannel',
        type: 'string',
        description: 'Communication channel',
        required: false
    },
    {name: 'additionalInfo.travelReason', type: 'string', description: 'Travel reason', required: false},

    {name: 'fromBookingEngine', type: 'boolean', description: 'Created from booking engine', required: false},
    {
        name: 'source',
        type: 'string',
        description: 'Where the reservation was created (BOOKING_ENGINE, GUEST_APP, DASHBOARD, PMS, SYSTEM)',
        required: false
    },

    {
        name: 'reservationRequestData.firstName',
        type: 'string',
        description: 'Request first name',
        required: false
    },
    {
        name: 'reservationRequestData.lastName',
        type: 'string',
        description: 'Request last name',
        required: false
    },
    {name: 'reservationRequestData.email', type: 'string', description: 'Request email', required: false},
    {name: 'reservationRequestData.phone', type: 'string', description: 'Request phone', required: false},

    {name: 'insertDate', type: 'date', description: 'Creation timestamp', required: true},
    {name: 'updateDate', type: 'date', description: 'Last update timestamp', required: false}
];

/**
 * The API enriches `RESERVATION_*` (primary profile) and `SURVEY_*` (the invitation's guest) payloads
 * with these; every field is empty when no profile is behind it.
 */
const guestOutputs: NodeOutput[] = [
    {name: 'guest.id', type: 'string', description: 'Guest profile ID', required: false},
    {name: 'guest.firstName', type: 'string', description: 'Guest first name', required: false},
    {name: 'guest.lastName', type: 'string', description: 'Guest last name', required: false},
    {name: 'guest.namePrefix', type: 'string', description: 'Guest name prefix (Mr., Mrs., etc.)', required: false},
    {name: 'guest.academicTitle', type: 'string', description: 'Guest academic title', required: false},
    {name: 'guest.gender', type: 'string', description: 'Guest gender', required: false},
    {name: 'guest.languageCode', label: 'Guest language', valueKind: 'language', type: 'string', description: 'Language the guest prefers', required: false},
    {name: 'guest.nationality', label: 'Nationality', valueKind: 'nationality', type: 'string', description: 'Guest nationality', required: false},
    {name: 'guest.email', type: 'string', description: 'Primary email address of the guest', required: false},
    {name: 'guest.phone', type: 'string', description: 'Primary phone number of the guest', required: false}
];

export const nodeOutputDefinitions: NodeOutputDefinition[] = [
    {
        nodeType: NodeType.FORM_SUBMITTED,
        outputs: [
            {name: 'formId', type: 'string', description: 'ID of the submitted form', required: true},
            {name: 'answers', type: 'object', description: 'Complete form submission data', required: true},
            {
                name: 'email',
                type: 'string',
                description: 'Email of the person who submitted',
                required: false
            },
            {
                name: 'phone',
                type: 'string',
                description: 'Phone of the person who submitted',
                required: false
            },
            {name: 'profileId', type: 'string', description: 'Linked profile id', required: false},
            {name: 'submittedAt', type: 'date', description: 'Timestamp when form was submitted', required: true}
        ]
    },
    {
        nodeType: NodeType.RESERVATION_CREATED,
        outputs: [...reservationOutputs, ...guestOutputs]
    },
    {
        nodeType: NodeType.RESERVATION_STATUS_CHANGED,
        outputs: [
            ...reservationOutputs,
            ...guestOutputs,
            {
                name: 'previousStatus',
                type: 'string',
                description: 'Status the reservation left (empty when the node fired for a creation)',
                required: false
            },
            {name: 'newStatus', type: 'string', description: 'Status the reservation reached', required: true},
            {name: 'changedAt', type: 'date', description: 'Moment the status changed', required: true}
        ]
    },
    {
        nodeType: NodeType.SCHEDULED_TRIGGER,
        outputs: [
            {name: 'scheduleType', type: 'string', description: 'Type of schedule (once, daily, weekly, monthly)', required: true},
            {name: 'scheduledTime', type: 'date', description: 'Originally scheduled time', required: true},
            {name: 'executionTime', type: 'date', description: 'Actual execution time', required: true},
            {name: 'triggerNodeId', type: 'string', description: 'ID of the trigger node', required: true},
            {name: 'flowId', type: 'string', description: 'ID of the automation flow', required: true},
            {name: 'flowName', type: 'string', description: 'Name of the automation flow', required: false},
            {name: 'tenantId', type: 'string', description: 'Tenant ID', required: false}
        ]
    },
    {
        nodeType: NodeType.MANUAL_TRIGGER,
        outputs: [
            {name: 'triggerTime', type: 'date', description: 'Exact time when the trigger fired', required: true},
            {name: 'triggerNodeId', type: 'string', description: 'ID of the trigger node', required: false},
            {name: 'flowId', type: 'string', description: 'ID of the automation flow', required: false}
        ]
    },
    {
        // Always an invited guest. npsClass, satisfactionClass and score stay empty until the version is classified;
        // they are service-recovery signals — never use them to pick who is asked for a public review.
        nodeType: NodeType.SURVEY_SUBMITTED,
        outputs: [
            {name: 'submissionId', type: 'string', description: 'ID of the submitted response', required: true},
            {name: 'surveyId', type: 'string', description: 'ID of the answered survey', required: true},
            {name: 'surveyName', type: 'string', description: 'Name of the answered survey', required: false},
            {name: 'dashboardUrl', type: 'string', description: 'Link to this survey evaluation in the dashboard', required: false},
            {
                name: 'surveyType',
                type: 'string',
                description: 'Survey type (PRE_STAY, POST_STAY, PRE_SERVICE, POST_SERVICE)',
                required: false
            },
            {name: 'surveyVersion', type: 'number', description: 'Version of the survey that was answered', required: false},
            {name: 'surveyLanguage', label: 'Survey language', valueKind: 'language', type: 'string', description: 'Language the response was given in', required: false},
            {name: 'invitationId', type: 'string', description: 'Invitation the response came from', required: false},
            {name: 'sessionId', type: 'string', description: 'Session the response was answered in', required: false},
            {name: 'profileId', type: 'string', description: 'Guest profile behind the response', required: false},
            {name: 'reservationId', type: 'string', description: 'Reservation the invitation belonged to', required: false},
            {name: 'sectorId', type: 'string', description: 'Sector the response belongs to', required: false},
            {
                name: 'guestCategoryIds',
                type: 'array',
                description: 'Ids of the guest categories the guest matched when invited',
                required: false
            },
            {
                name: 'guestCategories',
                type: 'array',
                description: 'Names of the guest categories the guest matched when invited',
                required: false
            },
            {
                name: 'npsClass',
                type: 'string',
                description: 'Worst NPS class in the response (PROMOTER, PASSIVE, DETRACTOR); empty when nothing is mapped',
                required: false,
                options: [
                    {value: 'PROMOTER', label: 'Promoter'},
                    {value: 'PASSIVE', label: 'Passive'},
                    {value: 'DETRACTOR', label: 'Detractor'}
                ]
            },
            {
                name: 'satisfactionClass',
                type: 'string',
                description: 'DISSATISFIED when any mapped satisfaction answer is dissatisfied, otherwise SATISFIED; empty when nothing is mapped',
                required: false,
                options: [
                    {value: 'SATISFIED', label: 'Satisfied'},
                    {value: 'DISSATISFIED', label: 'Dissatisfied'}
                ]
            },
            {
                name: 'score',
                type: 'number',
                description: 'Quality score of the response from 0 to 100, the average of its rated answers; empty when nothing is mapped',
                required: false
            },
            {
                name: 'answers',
                type: 'array',
                description: 'The given answers (questionId, type and the value fields); answers to sensitive questions are left out',
                required: true
            },
            {name: 'submittedAt', type: 'date', description: 'Moment the response was submitted', required: true},
            // Text for emails, written by the API in the organisation's language and time zone; empty when the source is.
            {name: 'scoreLabel', type: 'string', description: 'Score as text, rounded to a whole number, e.g. 94', required: false},
            {name: 'npsLabel', type: 'string', description: 'NPS class as a word in the organisation\'s language', required: false},
            {name: 'satisfactionLabel', type: 'string', description: 'Satisfaction class as a word in the organisation\'s language', required: false},
            {name: 'surveyLanguageName', type: 'string', description: 'Name of the response language in the organisation\'s language', required: false},
            {name: 'submittedAtLabel', type: 'string', description: 'Date and time of the response in the organisation\'s time zone, e.g. 08.10.2026 00:48', required: false},
            {name: 'organizationId', type: 'string', description: 'Organization ID', required: false},
            ...guestOutputs
        ]
    },
    {
        // Same `link` shape as SURVEY_REMINDER_DUE, so one email works against both.
        nodeType: NodeType.SURVEY_INVITE,
        outputs: [
            {name: 'invitationId', type: 'string', description: 'ID of the invitation', required: true},
            {name: 'surveyId', type: 'string', description: 'ID of the invited survey', required: true},
            {name: 'surveyName', type: 'string', description: 'Name of the invited survey', required: false},
            {name: 'surveyVersion', type: 'number', description: 'Version of the invited survey', required: false},
            {name: 'token', type: 'string', description: 'Invitation token', required: true},
            {name: 'link', type: 'string', description: 'Ready-to-send survey link for this invitation', required: true},
            {name: 'profileId', type: 'string', description: 'Guest profile the invitation belongs to', required: false},
            {name: 'reservationId', type: 'string', description: 'Reservation the invitation belongs to', required: false},
            {
                name: 'guestLanguage',
                label: 'Guest language',
                valueKind: 'language',
                type: 'string',
                description: 'Language of the guest',
                required: false
            },
            {
                name: 'reused',
                type: 'boolean',
                description: 'True when a live invitation already existed and no new link was issued',
                required: false
            }
        ]
    },
    {
        nodeType: NodeType.SURVEY_REMINDER_DUE,
        outputs: [
            {name: 'invitationId', type: 'string', description: 'ID of the unanswered invitation', required: true},
            {name: 'surveyId', type: 'string', description: 'ID of the invited survey', required: true},
            {name: 'surveyName', type: 'string', description: 'Name of the invited survey', required: false},
            {
                name: 'surveyType',
                type: 'string',
                description: 'Survey type (PRE_STAY, POST_STAY, PRE_SERVICE, POST_SERVICE, ANONYMOUS)',
                required: false
            },
            {name: 'surveyVersion', type: 'number', description: 'Version of the invited survey', required: false},
            {name: 'token', type: 'string', description: 'Invitation token', required: true},
            {name: 'link', type: 'string', description: 'Ready-to-send survey link for this invitation', required: true},
            {name: 'profileId', type: 'string', description: 'Guest profile the invitation was sent to', required: false},
            {name: 'reservationId', type: 'string', description: 'Reservation the invitation belongs to', required: false},
            {
                name: 'guestCategoryIds',
                type: 'array',
                description: 'Ids of the guest categories the guest matched when invited',
                required: false
            },
            {
                name: 'guestCategories',
                type: 'array',
                description: 'Names of the guest categories the guest matched when invited',
                required: false
            },
            {name: 'guestLanguage', type: 'string', description: 'Language of the guest', required: false},
            {
                name: 'status',
                type: 'string',
                description: 'How far the guest got (NOT_OPENED, OPENED, IN_PROGRESS)',
                required: true
            },
            {name: 'furthestQuestionIndex', type: 'number', description: 'Furthest question the guest reached', required: false},
            {name: 'sentAt', type: 'date', description: 'Moment the invitation was sent', required: true},
            {name: 'daysSinceSent', type: 'number', description: 'Whole days between sending and the reminder', required: true},
            {name: 'reminderAt', type: 'date', description: 'Moment the reminder came due', required: true},
            {name: 'organizationId', type: 'string', description: 'Organization ID', required: false},
            ...guestOutputs
        ]
    },

    {
        nodeType: NodeType.SEND_EMAIL,
        outputs: [
            {name: 'sent', type: 'number', description: 'Number of emails sent', required: true},
            {name: 'totalRecipients', type: 'number', description: 'Total number of recipients', required: true},
            {name: 'messages', type: 'array', description: 'Array of sent message details (messageId, recipient, profileId, status)', required: true},
            {name: 'timestamp', type: 'date', description: 'Timestamp when emails were sent', required: true}
        ]
    },
    {
        nodeType: NodeType.SEND_PUSH,
        outputs: [
            {name: 'sent', type: 'boolean', description: 'Whether the push step succeeded (also true when delivery is held in a dry run)', required: true},
            {name: 'totalRecipients', type: 'number', description: 'Total number of recipients', required: true},
            {name: 'message', type: 'string', description: 'Push notification message text', required: true},
            {name: 'notifications', type: 'array', description: 'Array of notification details (profileId, title, message, deeplink, status)', required: true},
            {name: 'timestamp', type: 'date', description: 'Timestamp when notifications were sent', required: true}
        ]
    },
    {
        nodeType: NodeType.TEAM_MEMBERS,
        outputs: [
            {name: 'id', type: 'string', description: 'ID of the team member', required: true},
            {name: 'firstName', type: 'string', description: 'First name', required: false},
            {name: 'lastName', type: 'string', description: 'Last name', required: false},
            {name: 'fullName', type: 'string', description: 'First and last name together', required: false},
            {name: 'email', type: 'string', description: 'Email address the person signs in with', required: false},
            {name: 'userType', type: 'string', description: 'User type (HOTEL_MANAGER, MARKETING_USER, …)', required: false},
            {
                name: 'interfaceLanguage',
                label: 'Interface language',
                valueKind: 'language',
                type: 'string',
                description: 'Language the person uses the dashboard in — localise the message with it',
                required: false
            },
            {name: 'permittedAppIds', type: 'array', description: 'Ids of the apps this person may open', required: false},
            {name: 'teamSize', type: 'number', description: 'How many team members the step fanned out to', required: true},
            {name: 'memberIndex', type: 'number', description: 'Position of this person in the fan-out (0-based)', required: true}
        ]
    },
    {
        // Built once in the parent run, before any fan-out; member runs read the same values.
        nodeType: NodeType.SURVEY_REPORT,
        outputs: [
            {name: 'surveyId', type: 'string', description: 'ID of the survey the report is about', required: true},
            {name: 'surveyName', type: 'string', description: 'Name of the survey', required: false},
            {name: 'dashboardUrl', type: 'string', description: 'Link to this survey evaluation in the dashboard', required: false},
            {name: 'period', type: 'string', description: 'The reporting period as text, e.g. 01.09.2026 – 30.09.2026', required: true},
            {name: 'responses', type: 'number', description: 'Responses in the reporting period', required: true},
            {name: 'score', type: 'number', description: 'Score of the reporting period from 0 to 100; empty when nothing could be scored', required: false},
            {name: 'nps', type: 'number', description: 'Net Promoter Score of the reporting period; empty when the survey has none', required: false},
            // Text for emails: a dash instead of an empty line when there is no figure.
            {name: 'scoreLabel', type: 'string', description: 'Score as text, e.g. 79; a dash when there is none', required: true},
            {name: 'npsLabel', type: 'string', description: 'NPS as text with its sign, e.g. +42; a dash when there is none', required: true},
            {name: 'language', type: 'string', description: 'Language the report is written in (de, en or it)', required: true},
            {name: 'artifactIds', type: 'array', description: 'Ids of generated files available as required email attachments', required: true},
            {name: 'fileNames', type: 'array', description: 'File names of the report files', required: true}
        ]
    },
    {
        nodeType: NodeType.SEND_TEAM_EMAIL,
        outputs: [
            {name: 'sent', type: 'boolean', description: 'Whether sending succeeded or was safely held', required: true},
            {name: 'status', type: 'string', description: 'Delivery outcome: sent, held, failed, delivery_unknown or skipped', required: true},
            {name: 'sentTo', type: 'string', description: 'Delivery address (the tester’s in a test run)', required: false},
            {name: 'recipient', type: 'string', description: 'The team member’s address', required: false},
            {name: 'memberName', type: 'string', description: 'The team member’s name', required: false},
            {name: 'statusCode', type: 'number', description: 'The mail service’s response code when available', required: false},
            {name: 'attachmentNames', type: 'array', description: 'File names of the attached files', required: true}
        ]
    },
    {
        nodeType: NodeType.FULL_SYNC_AUDIENCE,
        outputs: [
            {name: 'audiencesSynced', type: 'number', description: 'Number of active audiences processed', required: true}
        ]
    },
    {
        // One run per guest: that guest's profile plus its place in the fan-out.
        nodeType: NodeType.AUDIENCE,
        outputs: [
            {name: 'audienceName', type: 'string', description: 'Name of the audience this run came from', required: false},
            {name: 'audienceSize', type: 'number', description: 'How many guests the audience holds', required: true},
            {name: 'profileIndex', type: 'number', description: 'Position of this guest in the fan-out (0-based)', required: true},

            {name: 'id', type: 'string', description: 'ID of the profile', required: true},
            {name: 'namePrefix', type: 'string', description: 'Name prefix (Mr., Mrs., etc.)', required: false},
            {name: 'firstName', type: 'string', description: 'First name', required: false},
            {name: 'middleName', type: 'string', description: 'Middle name', required: false},
            {name: 'lastName', type: 'string', description: 'Last name', required: false},
            {name: 'lastNamePrefix', type: 'string', description: 'Last name prefix', required: false},
            {name: 'nameSuffix', type: 'string', description: 'Name suffix', required: false},
            {name: 'familiarName', type: 'string', description: 'Familiar/nickname', required: false},
            {name: 'shortname', type: 'string', description: 'Short name', required: false},
            {name: 'academicTitle', type: 'string', description: 'Academic title', required: false},
            {name: 'businessTitle', type: 'string', description: 'Business title', required: false},
            {name: 'department', type: 'string', description: 'Department', required: false},

            {name: 'gender', type: 'string', description: 'Gender', required: false},
            {name: 'birthDay', type: 'date', description: 'Birthday', required: false},
            {name: 'languageCode', label: 'Language', valueKind: 'language', type: 'string', description: 'Preferred language code', required: false},
            {name: 'nationality', label: 'Nationality', valueKind: 'nationality', type: 'string', description: 'Nationality', required: false},

            {name: 'statusCode', type: 'string', description: 'Profile status code', required: false},
            {name: 'profileType', type: 'string', description: 'Profile type', required: false},
            {name: 'vipCode', type: 'string', description: 'VIP code', required: false},

            {name: 'receiveEmails', type: 'boolean', description: 'Receives emails', required: false},
            {name: 'receiveNewsletters', type: 'boolean', description: 'Receives newsletters', required: false},
            {name: 'receiveSMS', type: 'boolean', description: 'Receives SMS', required: false},
            {name: 'receivePromoCalls', type: 'boolean', description: 'Receives promo calls', required: false},
            {name: 'receiveLetters', type: 'boolean', description: 'Receives letters', required: false},
            {name: 'receivePush', type: 'boolean', description: 'Receives push notifications', required: false},

            // emails/phones/addresses resolve to the primary entry.
            {name: 'emails.email', type: 'string', description: 'Primary email address', required: false},
            {name: 'emails.emailType', type: 'string', description: 'Primary email type', required: false},
            {name: 'emails.isPrimary', type: 'boolean', description: 'Is primary email', required: false},

            {name: 'phones.phoneNumberFull', type: 'string', description: 'Full phone number', required: false},
            {name: 'phones.phoneType', type: 'string', description: 'Phone type (MOBILE, TELEPHONE)', required: false},
            {name: 'phones.countryAccessCode', type: 'string', description: 'Country access code', required: false},
            {name: 'phones.areaCode', type: 'string', description: 'Area code', required: false},
            {name: 'phones.phoneNumberLocal', type: 'string', description: 'Local phone number', required: false},
            {name: 'phones.isPrimary', type: 'boolean', description: 'Is primary phone', required: false},

            {name: 'addresses.addressLine1', type: 'string', description: 'Address line 1', required: false},
            {name: 'addresses.cityName', type: 'string', description: 'City name', required: false},
            {name: 'addresses.stateProv', type: 'string', description: 'State/Province', required: false},
            {name: 'addresses.countryCode', label: 'Country', valueKind: 'country', type: 'string', description: 'Country code', required: false},
            {name: 'addresses.countryName', type: 'string', description: 'Country name', required: false},
            {name: 'addresses.postalCode', type: 'string', description: 'Postal/ZIP code', required: false},
            {name: 'addresses.addressType', type: 'string', description: 'Address type', required: false},
            {name: 'addresses.latitude', type: 'number', description: 'Latitude', required: false},
            {name: 'addresses.longitude', type: 'number', description: 'Longitude', required: false},
            {name: 'addresses.isPrimary', type: 'boolean', description: 'Is primary address', required: false},

            {name: 'company.name', type: 'string', description: 'Company name', required: false},
            {name: 'company.vatNumber', type: 'string', description: 'Company VAT number', required: false},

            {name: 'createdAt', type: 'date', description: 'Profile creation date', required: false},
            {name: 'updatedAt', type: 'date', description: 'Profile last update date', required: false},
            {name: 'archived', type: 'boolean', description: 'Is profile archived', required: false},
            {name: 'live', type: 'boolean', description: 'Is profile live', required: false},

            {
                name: 'currentStay.reservationId',
                type: 'string',
                description: 'Current stay reservation ID',
                required: false
            },
            {name: 'currentStay.startDay', label: 'Current stay start date', type: 'date', description: 'Current stay start date', required: false},
            {name: 'currentStay.endDay', label: 'Current stay end date', type: 'date', description: 'Current stay end date', required: false},
            {name: 'currentStay.roomId', type: 'string', description: 'Current stay room ID', required: false},
            {name: 'currentStay.status', type: 'string', description: 'Current stay status', required: false},

            {
                name: 'checkingInToday.reservationId',
                type: 'string',
                description: 'Checking in today reservation ID',
                required: false
            },
            {
                name: 'checkingInToday.startDay',
                type: 'date',
                description: 'Checking in today start date',
                required: false
            },
            {
                name: 'checkingOutToday.reservationId',
                type: 'string',
                description: 'Checking out today reservation ID',
                required: false
            },
            {
                name: 'checkingOutToday.endDay',
                type: 'date',
                description: 'Checking out today end date',
                required: false
            },

            {
                name: 'smartData.revenueStats.totalRevenue',
                type: 'number',
                description: 'Total revenue from all stays',
                required: false
            },
            {
                name: 'smartData.revenueStats.averageRevenuePerStay',
                type: 'number',
                description: 'Average revenue per stay',
                required: false
            },
            {
                name: 'smartData.revenueStats.averageRevenuePerNight',
                type: 'number',
                description: 'Average revenue per night',
                required: false
            },
            {
                name: 'smartData.revenueStats.totalRoomRevenue',
                type: 'number',
                description: 'Total room revenue',
                required: false
            },
            {
                name: 'smartData.revenueStats.totalServiceRevenue',
                type: 'number',
                description: 'Total service revenue',
                required: false
            },
            {
                name: 'smartData.revenueStats.totalFBRevenue',
                type: 'number',
                description: 'Total F&B revenue',
                required: false
            },
            {
                name: 'smartData.revenueStats.totalSpaRevenue',
                type: 'number',
                description: 'Total spa revenue',
                required: false
            },
            {
                name: 'smartData.revenueStats.averageDailyRate',
                type: 'number',
                description: 'Average daily rate',
                required: false
            },
            {
                name: 'smartData.revenueStats.lastStayTotalRevenue',
                type: 'number',
                description: 'Last stay total revenue',
                required: false
            },

            {name: 'smartData.stays.totalStays', type: 'number', description: 'Total number of stays', required: false},
            {
                name: 'smartData.stays.totalNights',
                type: 'number',
                description: 'Total number of nights stayed',
                required: false
            },
            {name: 'smartData.stays.lastStayDate', type: 'date', description: 'Last stay date', required: false},
            {name: 'smartData.stays.firstStayDate', type: 'date', description: 'First stay date', required: false},
            {name: 'smartData.stays.nextStayDate', type: 'date', description: 'Next stay date', required: false},
            {
                name: 'smartData.stays.averageStayDuration',
                type: 'number',
                description: 'Average stay duration in days',
                required: false
            },
            {
                name: 'smartData.stays.averageDaysBeforeBooking',
                type: 'number',
                description: 'Average days before booking',
                required: false
            },
            {
                name: 'smartData.stays.averageDaysBetweenStays',
                type: 'number',
                description: 'Average days between stays',
                required: false
            },
            {
                name: 'smartData.stays.staysLastYear',
                type: 'number',
                description: 'Number of stays in the last year',
                required: false
            },
            {
                name: 'smartData.stays.staysLastMonth',
                type: 'number',
                description: 'Number of stays in the last month',
                required: false
            },
            {
                name: 'smartData.stays.cancellationCount',
                type: 'number',
                description: 'Number of cancellations',
                required: false
            },
            {
                name: 'smartData.stays.cancellationRate',
                type: 'number',
                description: 'Cancellation rate',
                required: false
            },
            {
                name: 'smartData.stays.loyaltyProgramMember',
                type: 'boolean',
                description: 'Is loyalty program member',
                required: false
            },
            {name: 'smartData.stays.loyaltyPoints', type: 'number', description: 'Loyalty points', required: false},
            {name: 'smartData.stays.averageRating', type: 'number', description: 'Average rating', required: false},

            {
                name: 'smartData.travelCompanions.soloTravelFrequency',
                type: 'number',
                description: 'Solo travel frequency',
                required: false
            },
            {
                name: 'smartData.travelCompanions.coupleTravelFrequency',
                type: 'number',
                description: 'Couple travel frequency',
                required: false
            },
            {
                name: 'smartData.travelCompanions.familyTravelFrequency',
                type: 'number',
                description: 'Family travel frequency',
                required: false
            },
            {
                name: 'smartData.travelCompanions.groupTravelFrequency',
                type: 'number',
                description: 'Group travel frequency',
                required: false
            },
            {
                name: 'smartData.travelCompanions.averageGroupSize',
                type: 'number',
                description: 'Average group size',
                required: false
            },

            {
                name: 'smartData.newsletterData.subscribedInMaileon',
                type: 'boolean',
                description: 'Subscribed in Maileon',
                required: false
            },
            {
                name: 'smartData.newsletterData.targetGroupName',
                type: 'string',
                description: 'Newsletter target group',
                required: false
            },
            {
                name: 'smartData.newsletterData.organizationName',
                type: 'string',
                description: 'Newsletter organization',
                required: false
            },

            {name: 'smartData.isVip', type: 'boolean', description: 'Is VIP guest', required: false},
            {
                name: 'smartData.metaRepresentativeEmail',
                type: 'string',
                description: 'Meta representative email',
                required: false
            }
        ]
    },

    {
        nodeType: NodeType.CONTINUE_IF,
        outputs: [
            {name: 'evaluated', type: 'boolean', description: 'Whether the guest may continue', required: true},
            {name: 'reason', type: 'string', description: 'Why this guest continued or stopped', required: true}
        ]
    },
    {
        nodeType: NodeType.IF_CONDITION,
        outputs: [
            {name: 'evaluated', type: 'boolean', description: 'Result of the condition evaluation', required: true},
            {name: 'mode', type: 'string', description: 'Condition evaluation mode (STANDARD or AI)', required: true}
        ]
    },
    {
        nodeType: NodeType.WAIT,
        outputs: [
            {name: 'waitStartedAt', type: 'date', description: 'Timestamp when wait started', required: true},
            {name: 'waitEndAt', type: 'date', description: 'Timestamp when wait will end', required: true},
            {name: 'waitDuration', type: 'number', description: 'Duration of wait in milliseconds', required: true},
            {name: 'waitCompleted', type: 'boolean', description: 'Whether wait has completed', required: true}
        ]
    },
    {
        nodeType: NodeType.FINISH,
        outputs: [
            {name: 'finished', type: 'boolean', description: 'The run ended here', required: true}
        ]
    },
    {
        nodeType: NodeType.AFTER_EVERYONE,
        outputs: [
            {name: 'membersQueued', type: 'number', description: 'Members the fan-out started a run for', required: true},
            {name: 'membersSettled', type: 'number', description: 'Member runs that finished, failed ones included', required: true},
            {name: 'membersFailed', type: 'number', description: 'Member runs that failed', required: true}
        ]
    }
];

export const getNodeOutputs = (nodeType: NodeType): NodeOutput[] => {
    const definition = nodeOutputDefinitions.find(def => def.nodeType === nodeType);
    return definition?.outputs || [];
};
